import { useState, useRef } from "react";
import dayjs from "dayjs";
import { getCustomer, createCustomer } from "../../Core/customer";
import { upsertWaitlist, getWaitlist, getTableReservation } from "../../../../services";

/**
 * Provides waitlist and reservation management:
 * generateWaitlistId, saveToWaitlist, loadWaitlistForUI,
 * validateWaitlistForm, validateForm, loadReservations
 * and associated state.
 */
const useWaitlistReservation = ({
  tillData,
  cart,
  setCart,
  addNewCustomer,
  noOfPersons,
  reservePhone,
  tableList,
  reserveTableForm,
  setWaitlistDrawerOpen,
}) => {
  const [waitlistTab, setWaitlistTab] = useState("current");
  const [waitlistForm, setWaitlistForm] = useState({
    name: "",
    phone: "",
    guests: "",
    eta: "",
    tableNum: "",
  });
  const [waitlistErrors, setWaitlistErrors] = useState({});
  const [waitlistList, setWaitlistList] = useState([]);
  const [reservationList, setReservationList] = useState([]);
  const [errors, setErrors] = useState({});
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  // Ref is the real double-submit guard; the state only drives the UI. See the
  // matching note in useVoidHandler.js — setWaitlistSubmitting does not update the
  // captured closure synchronously, so two same-tick submits would both pass the
  // state check and enqueue the same guest onto the waitlist twice.
  const waitlistSubmittingRef = useRef(false);

  const generateWaitlistId = () => "WL-" + Date.now();

  const saveToWaitlist = async (guestData = null) => {
    if (!guestData && !validateWaitlistForm()) return;
    if (waitlistSubmittingRef.current || waitlistSubmitting) return;
    waitlistSubmittingRef.current = true;
    setWaitlistSubmitting(true);
    try {
      await saveToWaitlistInternal(guestData);
    } finally {
      waitlistSubmittingRef.current = false;
      setWaitlistSubmitting(false);
    }
  };

  const saveToWaitlistInternal = async (guestData) => {

    let customerId = null;

    if (guestData) {
      waitlistForm.waitlistId = guestData.waitlistId;
      waitlistForm.status = guestData.status;
      waitlistForm.name = guestData.name || waitlistForm.name || "";
      waitlistForm.phone = guestData.phone || waitlistForm.phone || "";
      waitlistForm.guests = guestData.guests || waitlistForm.guests || 0;
      waitlistForm.eta = guestData.eta || waitlistForm.eta || 0;
    }

    if (waitlistForm.phone && waitlistForm.phone.length === 10) {
      const response = await getCustomer(null, 2, waitlistForm.phone, null, () => {}, null, tillData);
      const list = response?.data?.data?.customerSearch || [];
      const match = list.find((x) => x.mobileNo?.toString() === waitlistForm.phone.toString());

      if (match) {
        customerId = match.cwrCustomerId;
      } else {
        const newCust = {
          name: waitlistForm.name || "",
          mobile: waitlistForm.phone,
          email: "",
          pincode: "",
          city: "",
          street: "",
          lastName: "",
        };

        await addNewCustomer(newCust);

        setCart((prev) => ({
          ...prev,
          customer: {
            cwrCustomerId: null,
            name: newCust.name,
            mobileNo: newCust.mobile,
          },
        }));

        localStorage.setItem(
          "cartObj",
          JSON.stringify({
            ...cart,
            customer: {
              cwrCustomerId: null,
              name: newCust.name,
              mobileNo: newCust.mobile,
            },
          })
        );

        customerId = null;
      }
    }

    const statusSource = guestData?.status || waitlistForm.status;
    const finalStatus = statusSource === "CL" ? "CL" : statusSource === "STD" ? "STD" : "WAT";

    const payload = {
      waitlistId: waitlistForm.waitlistId || generateWaitlistId(),
      bunitId: tillData.tillAccess.csBunit.csBunitId,
      guestName: waitlistForm.name,
      mobileNo: waitlistForm.phone,
      noOfGuests: waitlistForm.guests,
      estimateWaitTime: waitlistForm.eta,
      status: finalStatus,
      b2cCustomerId: cart.customer?.cwrCustomerId || null,
    };

    const res = await upsertWaitlist(payload);

    if (res?.status === "200") {
      const fresh = await loadWaitlistForUI();
      setWaitlistList(fresh);

      if (finalStatus === "WAT") {
        setWaitlistForm({ name: "", phone: "", guests: "", eta: "", tableNum: "" });
        setWaitlistErrors({});
        setWaitlistTab("current");
        if (setWaitlistDrawerOpen) setWaitlistDrawerOpen(false);
      }
    }
  };

  const loadWaitlistForUI = async () => {
    try {
      const rawList = await getWaitlist("0", "50");
      return rawList.map((item, index) => ({
        id: item.waitlistId ?? index + 1,
        name: item.guestName || "",
        phone: item.mobileNo || "",
        guests: item.noOfGuests || "",
        eta: item.estimateWaitTime || "",
        cwrCustomerId: item.b2cCustomerId || item.b2c_customer_id || item.cwrCustomerId || null,
        status: item.status,
        created: item.created,
        notified: false,
      }));
    } catch (err) {
      console.error("Error formatting UI waitlist:", err);
      return [];
    }
  };

  const validateWaitlistForm = () => {
    let err = {};
    if (!waitlistForm.name.trim()) err.name = "Please enter guest name";
    if (!waitlistForm.phone.trim()) err.phone = "Please enter phone number";
    if (!waitlistForm.guests) err.guests = "Please select guest size";
    if (!waitlistForm.eta.trim()) err.eta = "Please enter estimated wait time";
    setWaitlistErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateForm = () => {
    let newErrors = {};
    if (!reserveTableForm.getFieldValue("name")?.trim()) newErrors.guestName = "Please enter guest name";
    if (!reservePhone.trim()) newErrors.phone = "Please enter phone number";
    if (!reserveTableForm.getFieldValue("time")) newErrors.resTime = "Please select reservation time";
    if (!noOfPersons) newErrors.guestCount = "Please select number of guests";
    if (!reserveTableForm.getFieldValue("tableNum")) newErrors.tableNum = "Please select table";
    if (!reserveTableForm.getFieldValue("section")) newErrors.section = "Please select section";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadReservations = async () => {
    const selectedDate = dayjs().format("YYYY-MM-DD");
    let allReservations = [];

    const listNoTable = await getTableReservation(null, selectedDate);
    if (Array.isArray(listNoTable)) {
      allReservations.push(...listNoTable);
    }

    const uniqueReservations = Array.from(new Map(allReservations.map((r) => [r.fbTableReservationId, r])).values());

    const mapped = uniqueReservations.map((r) => {
      const timeRaw = r.resTime || r.reservationTime;
      const dateRaw = r.resDate || r.reservationDate;
      const statusKey = r.status ? r.status.toUpperCase() : "NEW";
      return {
        id: r.fbTableReservationId,
        tableId: r.fbTableId,
        sectionId: r.fbSectionId,
        name: r.customerName,
        phone: r.contactNo || "",
        guests: r.noOfPeople,
        date: dayjs(dateRaw).format("DD/MM/YYYY"),
        time: dayjs(timeRaw).format("DD/MM/YYYY, hh:mm A"),
        status: statusKey,
        statusLabel: statusKey === "NEW" ? "Upcoming" : statusKey === "CK" ? "Checked In" : statusKey === "NS" ? "No Show" : statusKey,
        tableName: r.created,
        section: r.guestType,
      };
    });

    setReservationList(mapped);
  };

  return {
    waitlistTab,
    setWaitlistTab,
    waitlistForm,
    setWaitlistForm,
    waitlistErrors,
    setWaitlistErrors,
    waitlistList,
    setWaitlistList,
    reservationList,
    setReservationList,
    errors,
    setErrors,
    generateWaitlistId,
    saveToWaitlist,
    waitlistSubmitting,
    loadWaitlistForUI,
    validateWaitlistForm,
    validateForm,
    loadReservations,
  };
};

export default useWaitlistReservation;
